import React, { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useLocaleNavigate } from '../../hooks/useLocaleNavigate';
import { Layout, ErrorBoundary } from '@lucid/lego';
import { useStore } from '../../store/store';
import styles from './LayoutWrapper.module.scss';

const LayoutWrapper: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const toggleTheme = useStore((state) => state.toggleTheme);
  const locale = useStore((state) => state.locale);
  const setLocale = useStore((state) => state.setLocale);
  const isSidebarCollapsed = useStore((state) => state.isSidebarCollapsed);
  const setSidebarCollapsed = useStore((state) => state.setSidebarCollapsed);
  const location = useLocation();
  const navigate = useLocaleNavigate();

  // Sync store locale with URL locale on mount and path changes
  useEffect(() => {
    const currentPath = location.pathname;
    // Match locale at start of path, with or without trailing slash
    const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})(?:\/|$)/i);

    if (localeMatch) {
      const urlLocale = localeMatch[1].toLowerCase();
      if (urlLocale !== locale) {
        setLocale(urlLocale);
      }
    } else {
      // No locale in URL, ensure store is set to default en-us
      if (locale !== 'en-us') {
        setLocale('en-us');
      }
    }
  }, [location.pathname, locale, setLocale]);

  const showRoleDropdown = import.meta.env.VITE_SHOW_ROLE_DROPDOWN === 'true';

  const handleLocaleChange = (newLocale: string) => {
    // Update store first
    setLocale(newLocale);

    // Navigate to the new locale URL using React Router
    const currentPath = location.pathname;
    const currentLocaleMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})(\/.*)?$/i);

    let newPath: string;
    if (currentLocaleMatch) {
      // There's an existing locale in the path
      const pathWithoutLocale = currentLocaleMatch[2] || '/';

      if (newLocale === 'en-us') {
        // For en-us, remove locale prefix
        newPath = pathWithoutLocale;
      } else {
        // Replace with new locale
        newPath = `/${newLocale}${pathWithoutLocale}`;
      }
    } else {
      // No locale in current path
      if (newLocale === 'en-us') {
        // For en-us, keep current path (no locale prefix)
        newPath = currentPath;
      } else {
        // Add locale prefix
        newPath = `/${newLocale}${currentPath}`;
      }
    }

    // Only navigate if the path actually changed
    if (newPath !== currentPath) {
      navigate(newPath);
    }
  };

  const handleError = (error: Error) => {
    console.error('Route Error:', error);
  };

  return (
    <Layout
      theme={theme}
      onToggleTheme={toggleTheme}
      showRoleDropdown={showRoleDropdown}
      currentPath={location.pathname}
      onLocaleChange={handleLocaleChange}
      isSidebarCollapsed={isSidebarCollapsed}
      onSidebarCollapseChange={setSidebarCollapsed}
    >
      <ErrorBoundary theme={theme} onError={handleError} showDetails={import.meta.env.DEV}>
        <Suspense fallback={<div className={styles.suspenseFallback} />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
};

export default LayoutWrapper;
